import React, { useContext, useEffect, useState } from 'react';
import { CreateCaseConstants } from 'src/constants/ProjectConstants';
import Alert, { AlertType } from 'src/components/Alert';
import { CREATE_CASE_PAGE_ROUTE } from 'src/constants/UrlConstants';
import { PageShellLayout, PageShell } from 'src/styles/pageLayoutStyles';
import { useNavigate } from 'react-router-dom';
import { useCreateCaseMutation } from 'src/state/slice/caseLifecycleApi';
import { reset as resetCreateCaseSlice } from 'src/state/slice/createCaseSlice';
import { useAppDispatch, useAppSelector } from 'src/state/store/hooks';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { ICreateCaseRequest } from 'src/state/slice/models/CaseLifecycleTypes';
import { logger } from 'src/logger';
import { FORMATTED_CASE_DETAILS_ROUTE } from 'src/constants/UrlConstants';
import { Breadcrumb } from 'src/components/Breadcrumb';
import styled from 'styled-components';
import { buildCreateCaseRequest } from './CreateCaseRequestFactory';
import CreateDetailsForm from 'src/components/Forms/CreateCase';
import { DDMAttributeContext } from 'src/integration/ddm/providers/DDMAttributesProvider';
import { scrollToTop } from 'src/utilities/PageLayout';
import { TranslationContext } from 'src/state/providers/TranslationProvider';
import URLFormatter from 'src/routes/helpers/URLFormatter';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import { PageContext } from 'src/state/providers/PageProvider';

export const CreateCaseHeader = styled.div`
  padding: 1rem 2rem 1rem 2rem; /* top right bottom left  */
  background-color: ${({ theme }) => theme.colors.Snow};
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

export const CreateCaseFormContainer = styled.div`
  margin-top: 10px;
  padding: 0px 2rem 0.5rem 2rem; /* top right bottom left  */
  background-color: ${({ theme }) => theme.colors.White};
`;

interface CreateCaseProps {}

const CreateCasePage: React.FC<CreateCaseProps> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [urlFormatter] = useState(new URLFormatter(window.location.href));
  const userContext = useContext(AuthContext);
  const pageContext = useContext(PageContext);
  const ddmAttributes = useContext(DDMAttributeContext);

  const { requestForm, referralInformation, attachments } = useAppSelector(
    (state) => state.createCase,
  );

  const [createCase, { isLoading: isSubmitting, isError, reset, data, error }] =
    useCreateCaseMutation();
  const { localizationContext } = useLocalizationContext();
  const redirectToCaseDetailsPage = (caseId: string, redirectUrl?: string) => {
    logger.info(`Redirect Url is ${redirectUrl} for CaseId: ${caseId} `);
    if (redirectUrl) {
      navigate(redirectUrl);
    } else {
      navigate(FORMATTED_CASE_DETAILS_ROUTE(caseId));
    }
  };

  //cleanup action
  useEffect(() => {
    return () => {
      reset(); //clear the API response from cache.
      dispatch(resetCreateCaseSlice()); // remove the state from the cache during unsubscription.
      logger.debug(`Refreshing the CreateCase slice after page unmounting.`);
    };
  }, []);

  const onCreateCaseClicked = () => {
    const additionalData: { [key: string]: string } = {
      caseLocale: localizationContext.getLocale(),
    };
    const payload = {
      ...requestForm,
      referralInformation,
    };
    const createCaseRequest: ICreateCaseRequest = buildCreateCaseRequest(
      userContext,
      pageContext,
      payload,
      additionalData,
      attachments,
      ddmAttributes,
    );
    logger.info(
      `Creating case for User: ${userContext.customerId}. Payload in context. `,
      { createCaseRequest },
    );
    createCase(createCaseRequest);
  };

  if (data) {
    logger.info(
      `Successfully created case for User: ${userContext.customerId}. Payload in context. `,
      { data },
    );
    redirectToCaseDetailsPage(data?.caseId, urlFormatter.getRedirectUrl());
  }

  if (isError) {
    logger.error(
      `Unable to create case for user: ${userContext.customerId}. Check details in context: `,
      error!,
    );
    scrollToTop();
  }

  const alertProps = {
    type: AlertType.ERROR,
    header: CreateCaseConstants.CREATE_CASE_FAILURE_HEADER,
    description: CreateCaseConstants.CREATE_CASE_FAILURE_DESCRIPTION,
  };

  const { createCaseBundle } = useContext(TranslationContext);

  return (
    <>
      <Breadcrumb page={CREATE_CASE_PAGE_ROUTE}></Breadcrumb>
      <PageShellLayout>
        <PageShell>
          {isError && (
            <Alert
              type={alertProps.type}
              header={createCaseBundle.getMessage(alertProps.header)}
              description={createCaseBundle.getMessage(alertProps.description)}
            ></Alert>
          )}
          <CreateCaseHeader>
            {createCaseBundle.getMessage(
              CreateCaseConstants.CREATE_CASE_HEADER,
            )}
          </CreateCaseHeader>
          <CreateCaseFormContainer>
            <CreateDetailsForm
              allowAttachments={true}
              showReferralInfo={true}
              onFormSubmission={onCreateCaseClicked}
              submitting={isSubmitting}
            />
          </CreateCaseFormContainer>
        </PageShell>
      </PageShellLayout>
    </>
  );
};

export default CreateCasePage;
